import React, { useEffect, useState } from 'react';
import {
  PhoneMailAttachment,
  PhoneMailHighlightField,
  PhoneMailHighlightItem,
  PhoneMailInboxItem,
  PhoneMailTriageAction,
  PhoneMailTriageMail,
  PhoneMailTriageReactionStep,
} from '../../types';
import {
  HighlightInEmailInput,
  normalizeHighlightInEmail,
} from '../../systems/phone-mail-triage';
import { PedaCategorySelect } from './PedaCategorySelect';
import styles from './scenario-studio.module.css';

interface StringListEditorProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  addLabel?: string;
}

const StringListEditor: React.FC<StringListEditorProps> = ({
  label,
  items,
  onChange,
  placeholder = '…',
  addLabel = '+ Ligne',
}) => {
  const update = (index: number, value: string) => {
    onChange(items.map((s, i) => (i === index ? value : s)));
  };

  return (
    <div className={styles.field}>
      <label>{label}</label>
      {items.map((line, index) => (
        <div key={index} className={styles.row} style={{ marginBottom: '0.35rem' }}>
          <input
            className={styles.input}
            style={{ flex: 1 }}
            value={line}
            onChange={(e) => update(index, e.target.value)}
            placeholder={placeholder}
          />
          <button type="button" onClick={() => onChange(items.filter((_, i) => i !== index))} title="Supprimer">
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className={styles.btnSecondary}
        onClick={() => onChange([...items, ''])}
      >
        {addLabel}
      </button>
    </div>
  );
};

interface HighlightInEmailEditorProps {
  items: PhoneMailHighlightItem[];
  onChange: (items: PhoneMailHighlightItem[]) => void;
}

const HighlightInEmailEditor: React.FC<HighlightInEmailEditorProps> = ({ items, onChange }) => {
  const normalizedItems = normalizeHighlightInEmail(items as HighlightInEmailInput[], {
    keepEmptyDrafts: true,
  }) ?? [];

  const update = (index: number, patch: Partial<PhoneMailHighlightItem>) => {
    onChange(normalizedItems.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  return (
    <div className={styles.field}>
      <label>Surlignage dans le mail (highlightInEmail)</label>
      {normalizedItems.map((item, index) => (
        <div key={index} className={styles.row} style={{ marginBottom: '0.35rem' }}>
          <input
            className={styles.input}
            style={{ flex: 1 }}
            value={item.term}
            onChange={(e) => update(index, { term: e.target.value })}
            placeholder="cultuure-gouv.fr"
          />
          <select
            className={styles.input}
            style={{ width: '7rem', flex: 'none' }}
            value={item.field}
            onChange={(e) => update(index, { field: e.target.value as PhoneMailHighlightField })}
          >
            <option value="from">from</option>
            <option value="content">content</option>
          </select>
          <button type="button" onClick={() => onChange(normalizedItems.filter((_, i) => i !== index))} title="Supprimer">
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className={styles.btnSecondary}
        onClick={() => onChange([...normalizedItems, { term: '', field: 'from' }])}
      >
        + Surlignage
      </button>
    </div>
  );
};

interface ReactionStepsEditorProps {
  steps: PhoneMailTriageReactionStep[];
  onChange: (steps: PhoneMailTriageReactionStep[]) => void;
}

const ReactionStepsEditor: React.FC<ReactionStepsEditorProps> = ({ steps, onChange }) => {
  const updateStep = (index: number, patch: Partial<PhoneMailTriageReactionStep>) => {
    onChange(steps.map((step, i) => (i === index ? { ...step, ...patch } : step)));
  };

  return (
    <div className={styles.field}>
      <label>Étapes réaction (surlignage optionnel + réactions)</label>
      {steps.length === 0 && (
        <p className={styles.hint}>Sans étape, le mail retourne à la boîte après le triage.</p>
      )}
      {steps.map((step, index) => (
        <div key={index} className={styles.card} style={{ marginBottom: '0.5rem' }}>
          <div className={styles.panelHeader}>
            <h6 style={{ margin: 0 }}>Étape {index + 1}</h6>
            <button type="button" onClick={() => onChange(steps.filter((_, i) => i !== index))} title="Supprimer">
              ×
            </button>
          </div>
          <HighlightInEmailEditor
            items={step.highlightInEmail ?? []}
            onChange={(highlightInEmail) =>
              updateStep(index, {
                highlightInEmail: highlightInEmail.length > 0 ? highlightInEmail : undefined,
              })
            }
          />
          <StringListEditor
            label="Réactions"
            items={step.reactions}
            onChange={(reactions) => updateStep(index, { reactions })}
            placeholder="Réplique du joueur…"
            addLabel="+ Réaction"
          />
        </div>
      ))}
      <button
        type="button"
        className={styles.btnSecondary}
        onClick={() => onChange([...steps, { reactions: [''] }])}
      >
        + Étape
      </button>
    </div>
  );
};

interface PhoneMailAttachmentsEditorProps {
  attachments: PhoneMailAttachment[];
  onChange: (attachments: PhoneMailAttachment[]) => void;
}

export const PhoneMailAttachmentsEditor: React.FC<PhoneMailAttachmentsEditorProps> = ({
  attachments,
  onChange,
}) => {
  const update = (index: number, patch: Partial<PhoneMailAttachment>) => {
    onChange(attachments.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  };

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h5>Pièces jointes</h5>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => onChange([...attachments, { name: '' }])}
        >
          + Pièce jointe
        </button>
      </div>
      {attachments.map((att, index) => (
        <div key={index} className={styles.card}>
          <div className={styles.row}>
            <div className={styles.field} style={{ flex: 2 }}>
              <label>Nom</label>
              <input
                className={styles.input}
                value={att.name}
                onChange={(e) => update(index, { name: e.target.value })}
              />
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <label>Taille (optionnel)</label>
              <input
                className={styles.input}
                value={att.size ?? ''}
                onChange={(e) => update(index, { size: e.target.value || undefined })}
                placeholder="4,2 Mo"
              />
            </div>
            <button type="button" onClick={() => onChange(attachments.filter((_, i) => i !== index))}>
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

interface PhoneMailInboxItemsEditorProps {
  items: PhoneMailInboxItem[];
  onChange: (items: PhoneMailInboxItem[]) => void;
}

export const PhoneMailInboxItemsEditor: React.FC<PhoneMailInboxItemsEditorProps> = ({
  items,
  onChange,
}) => {
  const update = (index: number, patch: Partial<PhoneMailInboxItem>) => {
    onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h5>Autres mails en boîte (inboxItems)</h5>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => onChange([...items, { from: '', subject: '' }])}
        >
          + Mail
        </button>
      </div>
      <p className={styles.hint}>Lignes supplémentaires visibles dans la boîte de réception pendant la lecture.</p>
      {items.map((it, index) => (
        <div key={index} className={styles.card}>
          <div className={styles.field}>
            <label>De</label>
            <input className={styles.input} value={it.from} onChange={(e) => update(index, { from: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Objet</label>
            <input className={styles.input} value={it.subject} onChange={(e) => update(index, { subject: e.target.value })} />
          </div>
          <div className={styles.field}>
            <label>Aperçu</label>
            <input className={styles.input} value={it.preview ?? ''} onChange={(e) => update(index, { preview: e.target.value || undefined })} />
          </div>
          <div className={styles.row}>
            <div className={styles.field} style={{ flex: 1 }}>
              <label>Heure</label>
              <input className={styles.input} value={it.receivedAt ?? ''} onChange={(e) => update(index, { receivedAt: e.target.value || undefined })} placeholder="07:12" />
            </div>
            <div className={styles.field}>
              <label>
                <input
                  type="checkbox"
                  checked={it.unread === true}
                  onChange={(e) => update(index, { unread: e.target.checked || undefined })}
                />
                {' '}Non lu
              </label>
            </div>
          </div>
          <button type="button" className={styles.btnDanger} onClick={() => onChange(items.filter((_, i) => i !== index))}>
            Supprimer
          </button>
        </div>
      ))}
    </div>
  );
};

interface PhoneMailTriageActionsEditorProps {
  actions: PhoneMailTriageAction[];
  onChange: (actions: PhoneMailTriageAction[]) => void;
  title?: string;
}

export const PhoneMailTriageActionsEditor: React.FC<PhoneMailTriageActionsEditorProps> = ({
  actions,
  onChange,
  title = 'Actions de triage (boutons)',
}) => {
  const update = (index: number, patch: Partial<PhoneMailTriageAction>) => {
    onChange(actions.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  };

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h5>{title}</h5>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => onChange([...actions, { label: '' }])}
        >
          + Action
        </button>
      </div>
      {actions.length === 0 && (
        <p className={styles.hint}>Sans actions, un bouton « Fermer » simple est affiché.</p>
      )}
      {actions.map((action, index) => (
        <div key={index} className={styles.card}>
          <div className={styles.field}>
            <label>Libellé du bouton</label>
            <input
              className={styles.input}
              value={action.label}
              onChange={(e) => update(index, { label: e.target.value })}
              placeholder="Légitime — archiver"
            />
          </div>
          <div className={styles.field}>
            <label>Boucliers pédagogiques (bonne réponse)</label>
            <input
              type="number"
              className={styles.input}
              value={action.pedaShields ?? ''}
              onChange={(e) =>
                update(index, { pedaShields: parseInt(e.target.value, 10) || undefined })
              }
            />
          </div>
          {(action.pedaShields ?? 0) > 0 && (
            <PedaCategorySelect
              value={action.pedaCategory ?? ''}
              onChange={(pedaCategory) => update(index, { pedaCategory })}
              required
            />
          )}
          <ReactionStepsEditor
            steps={
              action.reactionSteps
              ?? (action.reactions?.length
                ? [{
                    reactions: action.reactions,
                    highlightInEmail: action.highlightInEmail,
                  }]
                : [])
            }
            onChange={(reactionSteps) =>
              update(index, {
                reactionSteps: reactionSteps.length > 0 ? reactionSteps : undefined,
                reactions: undefined,
                highlightInEmail: undefined,
              })
            }
          />
          <button type="button" className={styles.btnDanger} onClick={() => onChange(actions.filter((_, i) => i !== index))}>
            Supprimer cette action
          </button>
        </div>
      ))}
    </div>
  );
};

interface PhoneMailTriageMailsEditorProps {
  mails: PhoneMailTriageMail[];
  onChange: (mails: PhoneMailTriageMail[]) => void;
}

export const PhoneMailTriageMailsEditor: React.FC<PhoneMailTriageMailsEditorProps> = ({
  mails,
  onChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const validIds = new Set(mails.map((m) => m.id));
    setExpandedIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [mails]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const update = (index: number, patch: Partial<PhoneMailTriageMail>) => {
    onChange(mails.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = [...mails];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    const [item] = next.splice(index, 1);
    next.splice(newIdx, 0, item!);
    onChange(next);
  };

  const showExpand = mails.length > 1;

  return (
    <div className={styles.phoneSection}>
      <div className={styles.panelHeader}>
        <h5>Session multi-mails (triage)</h5>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() =>
            onChange([
              ...mails,
              {
                id: `mail${mails.length + 1}`,
                from: '',
                subject: '',
                content: '',
                triageActions: [],
              },
            ])
          }
        >
          + Mail
        </button>
      </div>
      <p className={styles.hint}>
        Quand cette liste est renseignée, le moteur utilise le mode multi-mails (chaque mail a ses propres actions de triage).
      </p>
      {mails.map((mail, index) => {
        const expanded = !showExpand || expandedIds.has(mail.id);
        const summary = [mail.from, mail.subject].filter(Boolean).join(' — ');

        return (
          <div key={mail.id || index} className={styles.card}>
            <div className={styles.cardHeader}>
              {showExpand && (
                <button
                  type="button"
                  className={styles.expandToggle}
                  onClick={() => toggleExpanded(mail.id)}
                  aria-expanded={expanded}
                  aria-label={expanded ? 'Replier le mail' : 'Déplier le mail'}
                  title={expanded ? 'Replier' : 'Développer'}
                >
                  {expanded ? '▾' : '▸'}
                </button>
              )}
              <div className={styles.cardHeaderMain}>
                <h5 style={{ margin: 0 }}>Mail {index + 1}</h5>
                {showExpand && !expanded && summary && (
                  <span className={styles.cardMeta}>{summary}</span>
                )}
              </div>
              <div className={styles.inlineActions}>
                <button type="button" onClick={() => move(index, -1)} title="Monter">↑</button>
                <button type="button" onClick={() => move(index, 1)} title="Descendre">↓</button>
                <button type="button" onClick={() => onChange(mails.filter((_, i) => i !== index))} title="Supprimer">×</button>
              </div>
            </div>
            {expanded && (
              <div className={showExpand ? styles.cardBody : undefined}>
                <div className={styles.field}>
                  <label>ID (unique)</label>
                  <input
                    className={styles.input}
                    value={mail.id}
                    onChange={(e) => update(index, { id: e.target.value })}
                    placeholder="mail1"
                  />
                </div>
                <div className={styles.field}>
                  <label>De</label>
                  <input className={styles.input} value={mail.from} onChange={(e) => update(index, { from: e.target.value })} />
                </div>
                <div className={styles.field}>
                  <label>Objet</label>
                  <input className={styles.input} value={mail.subject} onChange={(e) => update(index, { subject: e.target.value })} />
                </div>
                <div className={styles.field}>
                  <label>Aperçu (liste)</label>
                  <input className={styles.input} value={mail.preview ?? ''} onChange={(e) => update(index, { preview: e.target.value || undefined })} />
                </div>
                <div className={styles.field}>
                  <label>Heure</label>
                  <input className={styles.input} value={mail.receivedAt ?? ''} onChange={(e) => update(index, { receivedAt: e.target.value || undefined })} placeholder="07:12" />
                </div>
                <div className={styles.field}>
                  <label>Contenu</label>
                  <textarea
                    className={styles.textarea}
                    rows={5}
                    value={mail.content}
                    onChange={(e) => update(index, { content: e.target.value })}
                  />
                </div>
                <PhoneMailTriageActionsEditor
                  actions={mail.triageActions}
                  onChange={(triageActions) => update(index, { triageActions })}
                  title="Actions de triage pour ce mail"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
